/**
 * LoginSchoolPage — School Portal Login.
 *
 * Step 1 — Email/Employee ID + password (POST /auth/login).
 * Step 2 — authenticator code OR recovery code (POST /auth/login/verify-2fa),
 *          shown only when the account has TOTP 2FA enabled (Admin / Super Admin).
 *
 * Route: /login
 */
import { useState, useMemo, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, type SchoolChoice, type RoleChoice, type StaffLoginResult } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Checkbox } from '../../components/ui/Checkbox';
import { getSchoolId, getSchoolName } from '../../services/api';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { ChevronRight } from 'lucide-react';

import { AuthLayout } from '../../components/auth/AuthLayout';
import { AuthCard } from '../../components/auth/AuthCard';
import { LegalFooterLinks } from '../../components/legal/LegalFooterLinks';
import { InstallAppButton } from '../../components/ui/InstallAppButton';
import { RotatingPlaceholder } from '../../components/auth/RotatingPlaceholder';
import { AuthInput } from '../../components/auth/AuthInput';
import { AuthButton } from '../../components/auth/AuthButton';
import {
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  ChevronLeftIcon,
} from '../../components/auth/AuthIcons';

function useSchoolId(): string | null {
  const { search } = useLocation();
  return useMemo(() => {
    const fromUrl = new URLSearchParams(search).get('school');
    if (fromUrl && fromUrl.trim().length > 0) return fromUrl.trim();
    const fromEnv = import.meta.env.VITE_DEFAULT_SCHOOL_ID;
    return typeof fromEnv === 'string' && fromEnv.length > 0 ? fromEnv : null;
  }, [search]);
}



// Returns the server's (English) error message when present, else undefined so the caller
// can fall back to a translated client message (server messages are a Tier-1.5 follow-up).
function extractError(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || undefined;
}

// First letter of the first two words, uppercased → e.g. "Test Academy" → "TA".
function schoolInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

export function LoginSchoolPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, verifyStaff2FA } = useAuth();
  const { showToast } = useToast();
  const schoolId = useSchoolId();

  const [email, setEmail] = useState('');
  // [UI-POLISH-3] Pauses the rotating placeholder while the field is in use.
  const [idFocused, setIdFocused] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [step, setStep] = useState<1 | 2>(1);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  // School-less login: when the loginId matches accounts in >1 school, the server
  // returns the list and we let the user pick (AUTH-L2b). null = picker closed.
  const [pickerSchools, setPickerSchools] = useState<SchoolChoice[] | null>(null);
  const [pickingSchoolId, setPickingSchoolId] = useState<string | null>(null);

  // ONB-4: when the loginId+password matches >1 role (e.g. a parent AND a teacher on one
  // number in one school), the server returns the roles and we let the user pick.
  const [pickerRoles, setPickerRoles] = useState<RoleChoice[] | null>(null);
  const [roleSchoolId, setRoleSchoolId] = useState<string | null>(null);
  const [pickingRole, setPickingRole] = useState<string | null>(null);

  // LOGIN-POLISH-1: the "New to Aisistoo?" block is collapsed by default (per-visit, no persistence).
  const [newOpen, setNewOpen] = useState(false);
  const reduceMotion = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches, []);

  // Display label for the "Signing into" line: prefer the school's name if we
  // have it persisted from a prior successful login for this same schoolId
  // (SchoolContext.tsx persists it after GET /schools/current). No public,
  // unauthenticated name-lookup endpoint exists (confirmed), so a first-time
  // visitor or a changed schoolId falls back to a truncated, clearly-labeled
  // ID rather than a bare UUID.
  const schoolLabel = useMemo(() => {
    if (!schoolId) return null;
    const persistedId = getSchoolId();
    const persistedName = getSchoolName();
    if (persistedId === schoolId && persistedName) return persistedName;
    return `${schoolId.slice(0, 8)}… (school id)`;
  }, [schoolId]);

  const goToDashboard = (mustChangePassword = false) => {
    if (mustChangePassword) {
      navigate('/change-password', { replace: true });
      return;
    }
    navigate('/', { replace: true });
  };

  // Route a login() result → picker, 2FA step, or dashboard. Shared by the initial
  // submit and the school-picker re-submit. `in` narrowing is safe here because
  // StaffLoginResult is an explicitly-declared union (unlike an inferred one).
  const handleLoginResult = (result: StaffLoginResult, usedSchoolId?: string) => {
    if ('multipleSchools' in result) {
      setPickerSchools(result.schools);
      return;
    }
    if ('multipleRoles' in result) {
      setPickerRoles(result.accounts);
      setRoleSchoolId(usedSchoolId ?? null);
      return;
    }
    setPickerSchools(null); // close the pickers if a pick led here
    setPickerRoles(null);
    if ('mustChangePassword' in result) {
      goToDashboard(result.mustChangePassword);
      return;
    }
    // requires2FA — server issued a challenge token for the second step
    if (result.challengeToken) {
      setChallengeToken(result.challengeToken);
      setCode('');
      setStep(2);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(t('common:auth.fill_all_fields'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Omit schoolId entirely when no school is pre-pinned (?school= / env) so the
      // server verifies across all accounts on this loginId. No undefined key sent.
      const result = await login({
        loginId: email.trim(),
        password,
        ...(schoolId ? { schoolId } : {}),
      });
      handleLoginResult(result, schoolId ?? undefined);
    } catch (err) {
      setError(extractError(err) ?? t('common:auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handlePickSchool = async (picked: SchoolChoice) => {
    setPickingSchoolId(picked.id);
    setError('');
    try {
      const result = await login({ loginId: email.trim(), password, schoolId: picked.id });
      handleLoginResult(result, picked.id);
    } catch (err) {
      showToast('error', extractError(err) ?? t('common:auth.invalid_credentials'));
    } finally {
      setPickingSchoolId(null);
    }
  };

  const handlePickRole = async (picked: RoleChoice) => {
    setPickingRole(picked.role);
    setError('');
    try {
      const result = await login({
        loginId: email.trim(),
        password,
        ...(roleSchoolId ? { schoolId: roleSchoolId } : {}),
        role: picked.role,
      });
      handleLoginResult(result, roleSchoolId ?? undefined);
    } catch (err) {
      showToast('error', extractError(err) ?? t('common:auth.invalid_credentials'));
    } finally {
      setPickingRole(null);
    }
  };

  const roleLabel = (rc: RoleChoice): string => {
    switch (rc.role) {
      case 'STUDENT': return t('common:auth.role_parent_student');
      case 'EMPLOYEE': return rc.employeeCategory === 'TEACHING' ? t('common:auth.role.teacher') : t('common:auth.role.staff');
      case 'ADMIN': return t('common:auth.role.admin');
      case 'SUPER_ADMIN': return t('common:auth.role.super_admin');
      default: return rc.role;
    }
  };

  const handleVerify = async () => {
    if (code.trim().length < 6 || !challengeToken) return;
    setError('');
    setLoading(true);
    try {
      const twoFaResult = await verifyStaff2FA(challengeToken, code.trim());
      goToDashboard(twoFaResult.mustChangePassword);
    } catch (err) {
      setError(extractError(err) ?? t('common:auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setChallengeToken(null);
    setCode('');
    setError('');
  };

  return (
    <AuthLayout>
      <AuthCard>
        {step === 1 ? (
          <>
            <div className="mb-7">
              <h1 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                {t('common:auth.welcome')}
              </h1>
              <p className="text-sm font-medium text-[var(--t3)]">
                {t('common:auth.sign_in_subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
              {schoolLabel && (
                <p className="text-[13px] font-semibold text-[var(--t2)] m-0 -mb-1">
                  {t('common:auth.signing_into')}: <span className="text-[var(--p)]">{schoolLabel}</span>
                </p>
              )}
              {/*
                [UI-POLISH-3] The rotating hint sits OVER the field, so the native placeholder is
                blanked — two of them would print on top of each other. It hides itself the instant
                the field is focused or holds anything, so nothing moves while someone is typing.
              */}
              <AuthInput
                label={t('common:auth.mobile_or_email')}
                type="text"
                placeholder=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setIdFocused(true)}
                onBlur={() => setIdFocused(false)}
                icon={<MailIcon />}
                autoComplete="username"
                overlay={
                  <RotatingPlaceholder
                    options={[t('common:auth.ph_rotate_mobile'), t('common:auth.ph_rotate_email')]}
                    staticText={t('common:auth.ph_rotate_static')}
                    paused={idFocused || email.length > 0}
                  />
                }
              />

              <AuthInput
                label={t('common:auth.password')}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('common:auth.password_ph')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<LockIcon />}
                rightIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                onRightIconClick={() => setShowPassword((p) => !p)}
                error={error}
                autoComplete="current-password"
              />

              <div className="flex justify-between items-center -mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    bare
                    checked={rememberMe}
                    onChange={setRememberMe}
                    size={16}
                    accent="var(--p)"
                  />
                  <span className="text-[13px] font-semibold text-[var(--t2)]">
                    {t('common:auth.remember_me')}
                  </span>
                </label>

                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-[13px] font-semibold text-[var(--p)] bg-transparent border-none cursor-pointer hover:underline"
                >
                  {t('common:auth.forgot_password')}
                </button>
              </div>

              <AuthButton type="submit" variant="primary" fullWidth loading={loading} size="lg">
                {t('common:auth.sign_in')}
              </AuthButton>
            </form>

            {/* LOGIN-POLISH-1: the first-login banner + the App-Owner main-column link are removed
                (banner content is covered by the "school uses Aisistoo" explainer below; Owner Portal
                moved to the footer). The New-to-Aisistoo paths are collapsed behind a quiet toggle —
                progressive disclosure keeps Login the sole primary action. */}
            <div className="mt-7">
              <button
                type="button"
                onClick={() => setNewOpen((v) => !v)}
                aria-expanded={newOpen}
                aria-controls="new-to-aisistoo-panel"
                data-testid="new-to-aisistoo-toggle"
                className="w-full inline-flex items-center justify-center gap-1.5 bg-transparent border-none cursor-pointer text-[12px] font-bold"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--t4)' }}
              >
                {t('common:auth.new_to_aisistoo')}
                <ChevronRight
                  size={13}
                  style={{ transform: newOpen ? 'rotate(-90deg)' : 'rotate(90deg)', transition: reduceMotion ? 'none' : 'transform 0.25s ease' }}
                />
              </button>
              <div
                id="new-to-aisistoo-panel"
                aria-hidden={!newOpen}
                style={{
                  maxHeight: newOpen ? 400 : 0,
                  overflow: 'hidden',
                  visibility: newOpen ? 'visible' : 'hidden',
                  transition: reduceMotion ? 'none' : 'max-height 0.3s ease',
                }}
              >
                {/* LOGIN-POLISH-2 C: two COMPACT rows — no headings/paragraph blocks. */}
                <div className="flex flex-col gap-3 pt-3.5" data-testid="login-signup-paths">
                  {/* Row 1 — muted single line (wraps on mobile) */}
                  <p className="text-[12px] font-medium text-[var(--t4)] m-0 leading-relaxed">{t('common:auth.cta_school_line')}</p>
                  {/* Row 2 — full-width outline button, no subtitle (the olympiad page's guard line carries it) */}
                  <AuthButton type="button" variant="secondary" fullWidth onClick={() => navigate('/olympiad/signup')}>
                    {t('common:auth.cta_new_button')}
                  </AuthButton>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-7">
              <button
                type="button"
                onClick={goBack}
                className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-[var(--t3)] text-[13px] font-semibold p-0 mb-4 hover:text-[var(--t2)]"
              >
                <ChevronLeftIcon />
                {t('common:buttons.back')}
              </button>
              <div
                className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-3.5"
                style={{ background: 'linear-gradient(135deg, var(--p), var(--p-dk))' }}
              >
                <LockIcon />
              </div>
              <h1 className="font-display text-[22px] font-extrabold tracking-[-0.03em] text-[var(--t1)] mb-1.5">
                {t('common:auth.twofa_title')}
              </h1>
              <p className="text-[13px] font-medium text-[var(--t3)] leading-relaxed">
                {t('common:auth.twofa_hint')}
              </p>
            </div>

            <input
              type="text"
              inputMode="numeric"
              autoFocus
              maxLength={9}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
              placeholder="123456"
              className="font-display"
              style={{ textAlign: 'center', letterSpacing: '0.3em', fontWeight: 700 }}
            />

            {error && (
              <div
                className="text-[13px] font-medium px-3 py-2 rounded-md mt-4"
                style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA' }}
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="mt-5">
              <AuthButton
                variant="primary"
                fullWidth
                size="lg"
                loading={loading}
                onClick={handleVerify}
                disabled={code.trim().length < 6}
              >
                {t('common:auth.verify_sign_in')}
              </AuthButton>
            </div>

            <p className="text-center text-xs font-medium text-[var(--t4)] mt-4 leading-relaxed">
              {t('common:auth.recovery_hint')}
            </p>
          </>
        )}
        {/* LOGIN-POLISH-1: legal links + the demoted Owner Portal link in one muted footer row.
            LegalFooterLinks stays shared/unchanged; the Owner Portal link is login-only. */}
        <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', fontSize: 11.5 }}>
          <LegalFooterLinks />
          <span style={{ color: 'var(--t4)' }}>·</span>
          <button
            type="button"
            onClick={() => navigate('/owner/login')}
            data-testid="footer-owner-portal"
            style={{ color: 'var(--t3)', fontWeight: 600, fontSize: 11.5, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          >
            {t('common:auth.owner_portal')}
          </button>
          {/*
            IB-2: a deliberate way to install, on the one screen a person reaches before they have
            an account. The auto-banner cannot be relied on here — its 14-day dismissal snooze
            survives uninstalling the app, and it is hidden outright above 1024px. This never nags;
            it hides itself once the app IS installed.
          */}
          <span style={{ color: 'var(--t4)' }}>·</span>
          <InstallAppButton style={{ fontSize: 11.5 }} />
        </div>
      </AuthCard>

      <BottomSheet
        open={pickerSchools !== null}
        onClose={() => setPickerSchools(null)}
        title={t('common:auth.choose_school')}
      >
        <div className="flex flex-col" style={{ fontFamily: 'var(--font-body)', gap: 10 }}>
          <p className="text-[13px] font-medium text-[var(--t3)] m-0" style={{ marginBottom: 4 }}>
            {t('common:auth.multi_school_hint')}
          </p>
          {pickerSchools?.map((s) => {
            const picking = pickingSchoolId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                data-testid="school-picker-card"
                disabled={pickingSchoolId !== null}
                onClick={() => handlePickSchool(s)}
                className={`school-picker-card w-full flex items-center gap-3 rounded-2xl border bg-[var(--surface-2)] text-left cursor-pointer transition-colors active:scale-[0.99] disabled:opacity-60 ${picking ? 'border-[var(--p)]' : 'border-[var(--border)]'}`}
                style={{ padding: '14px 16px' }}
              >
                <span
                  className="shrink-0 flex items-center justify-center rounded-xl text-white text-[14px] font-bold"
                  style={{ width: 40, height: 40, background: 'linear-gradient(135deg, var(--p), #8B5CF6)' }}
                >
                  {schoolInitials(s.name)}
                </span>
                <span className="flex-1 min-w-0 flex flex-col">
                  <span className="text-[15px] font-semibold text-[var(--t1)] truncate">{s.name}</span>
                  <span className="text-[12px] font-medium text-[var(--t3)]">{t('common:auth.tap_to_sign_in')}</span>
                </span>
                {picking ? (
                  <span className="shrink-0 text-[12px] font-semibold text-[var(--p)]">{t('common:auth.signing_in')}</span>
                ) : (
                  <ChevronRight size={18} className="shrink-0 text-[var(--t3)]" />
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>

      <BottomSheet
        open={pickerRoles !== null}
        onClose={() => setPickerRoles(null)}
        title={t('common:auth.choose_role')}
      >
        <div className="flex flex-col" style={{ fontFamily: 'var(--font-body)', gap: 10 }}>
          <p className="text-[13px] font-medium text-[var(--t3)] m-0" style={{ marginBottom: 4 }}>
            {t('common:auth.multi_role_hint')}
          </p>
          {pickerRoles?.map((rc) => {
            const picking = pickingRole === rc.role;
            return (
              <button
                key={rc.role}
                type="button"
                data-testid="role-picker-card"
                disabled={pickingRole !== null}
                onClick={() => handlePickRole(rc)}
                className={`role-picker-card w-full flex items-center gap-3 rounded-2xl border bg-[var(--surface-2)] text-left cursor-pointer transition-colors active:scale-[0.99] disabled:opacity-60 ${picking ? 'border-[var(--p)]' : 'border-[var(--border)]'}`}
                style={{ padding: '14px 16px' }}
              >
                <span
                  className="shrink-0 flex items-center justify-center rounded-xl text-white text-[14px] font-bold"
                  style={{ width: 40, height: 40, background: 'linear-gradient(135deg, var(--p), #8B5CF6)' }}
                >
                  {roleLabel(rc).charAt(0)}
                </span>
                <span className="flex-1 min-w-0 flex flex-col">
                  <span className="text-[15px] font-semibold text-[var(--t1)] truncate">{roleLabel(rc)}</span>
                  <span className="text-[12px] font-medium text-[var(--t3)]">{t('common:auth.tap_to_sign_in')}</span>
                </span>
                {picking ? (
                  <span className="shrink-0 text-[12px] font-semibold text-[var(--p)]">{t('common:auth.signing_in')}</span>
                ) : (
                  <ChevronRight size={18} className="shrink-0 text-[var(--t3)]" />
                )}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </AuthLayout>
  );
}

export default LoginSchoolPage;